import React from 'react';
import { Helmet } from 'react-helmet-async';
import { generatePath, Link } from 'react-router-dom';
import EmployeeLink from '@/components/common/EmployeeLink';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import translate from '@/lib/intl/translate';
import type Task from '@/types/Task';
import { formatDate } from '@/utils/dates';

const EMPLOYEE_LINK_CLASSES = {
  link: 'type-body3',
};

export default function TaskContent({
  task: {
    constituents = [],
    developers: assignees = [],
    dueDate,
    taskId,
    taskNotes,
    taskTitle,
  },
}: {
  task: Task;
}) {
  return (
    <div
      className="border-t border-grey-300 pt-5"
      data-testid={`entity-details-content-${taskId}`}
    >
      <Helmet>
        <title>
          {translate('TASK')} | {taskTitle}
        </title>
      </Helmet>
      <h3 className="mb-6 type-h3">{taskTitle}</h3>
      <div className="mb-4 grid grid-cols-2 gap-3">
        {dueDate && (
          <>
            <span className="type-body4">{translate('TASKS_DUE_DATE')}</span>
            <span className="type-body3">
              {formatDate(dueDate, 'MMMM d, y')}
            </span>
          </>
        )}

        {!!assignees.length && (
          <>
            <span className="type-body4">{translate('TASKS_ASSIGNEE')}</span>
            <span className="type-body3">
              {assignees.map(({ displayName, userId }, i) => (
                <React.Fragment key={userId}>
                  <EmployeeLink
                    key={userId}
                    classes={EMPLOYEE_LINK_CLASSES}
                    displayName={displayName}
                    userId={userId}
                  />
                  {assignees.length > 1 && i + 1 < assignees.length && ', '}
                </React.Fragment>
              ))}
            </span>
          </>
        )}
        {!!constituents.length && (
          <>
            <span className="type-body4">{translate('TASKS_CONSTITUENT')}</span>
            <span className="type-body3">
              {constituents.map((constituent) => (
                <Link
                  to={generatePath(AuthRoutes.CONSTITUENT, {
                    constituentId: constituent.constituentId,
                  })}
                >
                  <div className="text-primary-main">
                    {constituent.displayName}
                  </div>
                </Link>
              ))}
            </span>
          </>
        )}
      </div>
      {taskNotes && (
        <>
          <h3 className="mt-9 mb-4 type-h3">{translate('TASKS_NOTES')}</h3>
          <div className="mb-6 whitespace-pre-line type-body1">{taskNotes}</div>
        </>
      )}
    </div>
  );
}
